import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { endPoints } from "../../constant/Environment";
import { getAllData, addData } from "../../Utility/API";
import { useSnackbarContext } from "../../component/SnackbarContext";
import { setLoginData } from "../../redux/Reducers/AuthReducer/authSplice";
import useCountdown from "./components/useCountdown";
import LotterySelector from "./components/LotterySelector";
import LotteryHero from "./components/LotteryHero";
import NoActiveLottery from "./components/NoActiveLottery";
import NumberPicker from "./components/NumberPicker";
import TicketsPanel from "./components/TicketsPanel";
import ConfirmTicketDialog from "./components/ConfirmTicketDialog";

const Lottery = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarContext() || {};
  const authState = useSelector((state) => state.auth);
  const { isAuthenticated, IsLoginData } = authState || {};

  const [activeLottery, setActiveLottery] = useState(null);
  const [allActiveLotteries, setAllActiveLotteries] = useState([]);
  const [lotteryLoaded, setLotteryLoaded] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  // GET_ALL_ACTIVE_LOTTERY drives the switcher when more than one lottery is live;
  // GET_ACTIVE_LOTTERY (the earliest OPEN one) is the fallback single entry.
  const entries = useMemo(() => {
    const list =
      Array.isArray(allActiveLotteries) && allActiveLotteries.length
        ? allActiveLotteries
        : activeLottery?.id != null
        ? [activeLottery]
        : [];
    return list.map((l) => ({ ...l, key: `live-${l?.id}` }));
  }, [allActiveLotteries, activeLottery]);

  const lottery = useMemo(
    () => entries.find((e) => e?.key === selectedKey) || entries[0] || null,
    [entries, selectedKey]
  );

  const maxNumber = lottery?.maxNumber || 30;
  const ballCount = lottery?.ballCount || 3;
  // ticketPriceDisplay/prizeDisplay are formatted server-side in the player's own account
  // currency (e.g. "Rs 83.12"); only fall back to a raw "$" figure when the backend couldn't
  // resolve a currency for this request (e.g. logged-out visitor).
  const ticketPriceLabel =
    lottery?.ticketPriceDisplay || (lottery?.ticketPrice != null ? `$${lottery.ticketPrice}` : null);
  const prizeLabel = lottery?.prizeDisplay || (lottery?.prizeSc != null ? `${lottery.prizeSc} SC` : null);
  const countdown = useCountdown(lottery?.drawTime);

  // Ticket purchase only ever debits ubalance (the cash/redeemable bucket) server-side, never
  // bonus_balance - so that's the only figure relevant to "can this player afford a ticket".
  // ubalance is stored in the player's own account currency, so compare against
  // ticketPriceConverted (same currency), not the raw USD ticketPrice.
  const cashBalance = IsLoginData?.account?.ubalance ?? 0;
  const ticketPriceForBalanceCheck = lottery?.ticketPriceConverted ?? lottery?.ticketPrice;
  const hasInsufficientBalance =
    isAuthenticated && ticketPriceForBalanceCheck != null && cashBalance < ticketPriceForBalanceCheck;
  const lastResult = results?.[0] || null;
  const resultsByLotteryId = useMemo(() => {
    const map = {};
    results?.forEach((r) => {
      if (r?.id != null) map[r.id] = r;
    });
    return map;
  }, [results]);
  const pendingTickets = useMemo(
    () => myTickets.filter((t) => t?.status === "PENDING"),
    [myTickets]
  );
  const pastTickets = useMemo(
    () => myTickets.filter((t) => t?.status !== "PENDING"),
    [myTickets]
  );

  // Every response from this API is wrapped as {status, message, data} by a global
  // ResponseBodyAdvice, even for plain GET DTOs/lists - unwrap it the same way the
  // HappyHour screen's unwrapStatus/unwrapRecap helpers already do in this app.
  const unwrap = (res) => (res && res.status === "success" ? res.data : null);

  const fetchActiveLottery = async () => {
    try {
      const response = await getAllData(endPoints.api.GET_ACTIVE_LOTTERY);
      const data = unwrap(response);
      setActiveLottery(data?.id != null ? data : null);
    } catch (error) {
      setActiveLottery(null);
    } finally {
      setLotteryLoaded(true);
    }
  };

  const fetchAllActiveLotteries = async () => {
    try {
      const response = await getAllData(endPoints.api.GET_ALL_ACTIVE_LOTTERY);
      const data = unwrap(response);
      setAllActiveLotteries(Array.isArray(data) ? data : []);
    } catch (error) {
      setAllActiveLotteries([]);
    }
  };

  // Pull the account fresh so cash/bonus shown here and in the header match
  // what the purchase just deducted.
  const refreshUser = async () => {
    if (!IsLoginData?.idUser) return;
    try {
      const response = await getAllData(
        `${endPoints.api.GET_BY_USERID}/${IsLoginData.idUser}`
      );
      if (response?.data) {
        dispatch(setLoginData({ user: response.data, isAuthenticated: true }));
      }
    } catch (error) {
      console.error("lottery balance refresh failed", error);
    }
  };

  const fetchMyTickets = async () => {
    if (!isAuthenticated || !IsLoginData?.idUser) {
      setMyTickets([]);
      return;
    }
    try {
      const response = await getAllData(
        `${endPoints.api.GET_MY_LOTTERY_TICKETS}/${IsLoginData.idUser}`
      );
      const data = unwrap(response);
      setMyTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      setMyTickets([]);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await getAllData(endPoints.api.GET_LOTTERY_RESULTS);
      const data = unwrap(response);
      setResults(Array.isArray(data) ? data : []);
    } catch (error) {
      setResults([]);
    }
  };

  useEffect(() => {
    fetchActiveLottery();
    fetchAllActiveLotteries();
    fetchResults();
  }, []);

  useEffect(() => {
    fetchMyTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    setSelected([]);
  }, [lottery?.key]);

  const toggleNumber = (num) => {
    setSelected((prev) => {
      if (prev.includes(num)) return prev.filter((n) => n !== num);
      if (prev.length >= ballCount) return prev;
      return [...prev, num];
    });
  };

  const quickPick = () => {
    const picks = [];
    while (picks.length < ballCount) {
      const n = Math.floor(Math.random() * maxNumber) + 1;
      if (!picks.includes(n)) picks.push(n);
    }
    setSelected(picks);
  };

  const handleBuy = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (hasInsufficientBalance) {
      navigate("/buy-coins");
      return;
    }
    if (!lottery?.id) return;

    const uniqueCount = new Set(selected).size;
    const inRange = selected.every((n) => Number.isInteger(n) && n >= 1 && n <= maxNumber);

    if (selected.length !== ballCount || uniqueCount !== ballCount || !inRange) {
      showErrorSnackbar?.(`Pick ${ballCount} unique numbers between 1 and ${maxNumber}`);
      return;
    }
    setConfirmOpen(true);
  };

  const confirmBuy = async () => {
    setBuying(true);
    try {
      const response = await addData(endPoints.api.BUY_LOTTERY_TICKET, {
        lotteryId: lottery?.id,
        numbers: selected,
      });
      if (response?.data?.status === "success") {
        showSuccessSnackbar?.(response?.data?.message || "Ticket purchased. Good luck!");
        setSelected([]);
        setConfirmOpen(false);
        fetchActiveLottery();
        fetchAllActiveLotteries();
        fetchMyTickets();
        // The ticket was paid for from the wallet, so refresh the balance the
        // header and this page read from.
        refreshUser();
      } else {
        showErrorSnackbar?.(
          response?.data?.message || "Could not create your ticket. Please contact support."
        );
      }
    } catch (error) {
      showErrorSnackbar?.("Could not create your ticket. Please contact support.");
    } finally {
      setBuying(false);
    }
  };

  return (
    <main className="inner-body inner_space">
      {lotteryLoaded && !entries?.length ? (
        <NoActiveLottery isAuthenticated={isAuthenticated} lastResult={lastResult} />
      ) : (
        <>
          <LotterySelector entries={entries} selectedKey={lottery?.key} onSelect={setSelectedKey} />
          <LotteryHero
            lottery={lottery}
            isAuthenticated={isAuthenticated}
            hasMultiple={entries?.length > 1}
            countdown={countdown}
            lastResult={lastResult}
            maxNumber={maxNumber}
            prizeLabel={prizeLabel}
            ticketPriceLabel={ticketPriceLabel}
          />
        </>
      )}

      <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ mt: { xs: 1.5, md: 2 }, mb: { xs: 1.5, md: 2.5 } }}>
        {lottery && (
          <NumberPicker
            maxNumber={maxNumber}
            ballCount={ballCount}
            ticketPriceLabel={ticketPriceLabel}
            selected={selected}
            onToggleNumber={toggleNumber}
            onQuickPick={quickPick}
            onClear={() => setSelected([])}
            onBuy={handleBuy}
            countdownEnded={countdown?.ended}
            insufficientBalance={hasInsufficientBalance}
          />
        )}

        <TicketsPanel
          md={lottery ? 5 : 12}
          pendingTickets={pendingTickets}
          pastTickets={pastTickets}
          resultsByLotteryId={resultsByLotteryId}
          maxNumber={maxNumber}
        />
      </Grid>

      <ConfirmTicketDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        selected={selected}
        maxNumber={maxNumber}
        lottery={lottery}
        prizeLabel={prizeLabel}
        ticketPriceLabel={ticketPriceLabel}
        buying={buying}
        onConfirm={confirmBuy}
      />
    </main>
  );
};

export default Lottery;
